import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, Form, Row, Col, Badge, Alert, Spinner, Pagination } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  Search,
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  Save,
  User,
  Package,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Scale,
  ArrowLeft,
  Clock,
  Users,
  DollarSign,
  Layers,
  Phone,
  Mail,
  MapPin,
  Truck,
  ChevronRight,
  X,
  Sparkles,
} from "lucide-react";
import FullPageSpinner from "../layout/FullPageSpinner";
import ConfirmModal from "../layout/ConfirmModal";
import { ROLES } from "../../utils/constants";
import {
  fetchUserCartApi,
  updateUserCartBundleApi,
  deleteUserCartBundleApi,
  fetchStudentBundlesApi,
  fetchAllBundles,
  fetchAdminCartListApi,
  clearAdminUserCartApi,
  fetchProductSizeWeights,
} from "../../actions/product";
import { fetchALLStudent, getParentByStudentUsid } from "../../actions/student";
import { fetchGlobalSettings } from "../../actions/admin";
import { formatDateTime } from "../../services/helper";
import "./CartManagement.css";

const CartManagement = () => {
  const { user } = useSelector((state) => state.auth);

  // Permissions check
  const hasPermission = (permission) => {
    if (user?.role === ROLES.SUPER_ADMIN) return true;
    return user?.permissions?.[permission] === true;
  };

  const canView = user?.role === ROLES.SUPER_ADMIN || hasPermission("cart_management_view");
  const canEdit = user?.role === ROLES.SUPER_ADMIN || hasPermission("cart_edit") || hasPermission("cart_management_view");
  const canDelete = user?.role === ROLES.SUPER_ADMIN || hasPermission("cart_delete") || hasPermission("cart_management_view");

  // Global State
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // Active Carts List State (Latest 10)
  const [activeCarts, setActiveCarts] = useState([]);
  const [cartsTotal, setCartsTotal] = useState(0);
  const [cartsPage, setCartsPage] = useState(1);
  const [cartsLimit] = useState(10);
  const [cartsTotalPages, setCartsTotalPages] = useState(1);
  const [loadingActiveCarts, setLoadingActiveCarts] = useState(false);
  const [listSearchTerm, setListSearchTerm] = useState("");

  // Search State for Specific User Lookup
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilterType, setSearchFilterType] = useState("USID");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Selected User / Cart Context
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [cartItemsState, setCartItemsState] = useState([]);
  const [loadingCart, setLoadingCart] = useState(false);

  // Confirmation Modals
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const [parentToClear, setParentToClear] = useState(null);

  // Add Modal State (Bundles and Single Items)
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalMode, setModalMode] = useState("bundle"); // 'bundle' | 'product'
  const [availableBundles, setAvailableBundles] = useState([]);
  const [assignedBundles, setAssignedBundles] = useState([]);
  const [availableSingleItems, setAvailableSingleItems] = useState([]);
  const [assignedSingleItems, setAssignedSingleItems] = useState([]);
  const [itemSourceFilter, setItemSourceFilter] = useState("all"); // 'all' | 'assigned'
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [modalStudent, setModalStudent] = useState(null);
  const [selectedItemToAdd, setSelectedItemToAdd] = useState(null);
  const [modalProductSizes, setModalProductSizes] = useState({});
  const [modalProductQuantities, setModalProductQuantities] = useState({});
  const [addingItem, setAddingItem] = useState(false);
  const [fetchingCatalog, setFetchingCatalog] = useState(false);

  // Add Item to existing bundle card state
  const [showAddToBundleModal, setShowAddToBundleModal] = useState(false);
  const [extraProductList, setExtraProductList] = useState([]);
  const [targetBundleItem, setTargetBundleItem] = useState(null);
  const [selectedExtraProduct, setSelectedExtraProduct] = useState(null);
  const [selectedExtraSize, setSelectedExtraSize] = useState("");
  const [selectedExtraQty, setSelectedExtraQty] = useState(1);
  const [extraProductSearch, setExtraProductSearch] = useState("");
  const [sizeWeights, setSizeWeights] = useState([]);

  // Fetch global settings and product size weights
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetchGlobalSettings();
        const settingsData = res?.settings || res?.data || res;
        if (settingsData) {
          setSettings({
            enableHomeDelivery: settingsData.enableHomeDelivery ?? true,
            homeDeliveryPrice: settingsData.homeDeliveryPrice ?? 0,
            additionalBundlePrice: settingsData.additionalBundlePrice ?? 0,
            perKgRate: settingsData.perKgRate ?? 80,
            weightSlabs: Array.isArray(settingsData.weightSlabs) ? settingsData.weightSlabs : [],
            ...settingsData,
          });
        }
      } catch (err) {
        console.error("Failed to fetch settings:", err);
      }
    };

    const loadWeights = async () => {
      try {
        const sw = await fetchProductSizeWeights();
        if (Array.isArray(sw)) {
          setSizeWeights(sw);
        }
      } catch (err) {
        console.error("Failed to fetch size weights:", err);
      }
    };

    loadSettings();
    loadWeights();
  }, []);

  // Quick lookup map for size weights: "productId_size" -> weight in kg
  const sizeWeightsMap = useMemo(() => {
    const map = {};
    if (Array.isArray(sizeWeights)) {
      sizeWeights.forEach((item) => {
        const pId = item.productId || item.product_id;
        const sz = String(item.size || "").trim().toLowerCase();
        const wt = parseFloat(item.weight) || 0;
        if (wt > 0) {
          if (pId && sz) {
            map[`${pId}_${sz}`] = wt;
          }
          if (pId && (!map[pId] || wt > map[pId])) {
            map[pId] = wt;
          }
        }
      });
    }
    return map;
  }, [sizeWeights]);

  // Reliable weight calculator per item unit in kg
  const getProductWeightKg = useCallback(
    (productId, size, fallbackWeight = 0) => {
      const pId = productId;
      const sz = String(size || "").trim().toLowerCase();
      if (pId && sz && sizeWeightsMap[`${pId}_${sz}`] > 0) {
        return sizeWeightsMap[`${pId}_${sz}`];
      }
      if (pId && sizeWeightsMap[pId] > 0) {
        return sizeWeightsMap[pId];
      }
      const rawW = parseFloat(fallbackWeight) || 0;
      if (rawW > 0) {
        return rawW > 10 ? rawW / 1000 : rawW;
      }
      // Standard apparel fallback weight for school uniform items: 0.25 kg (250g)
      return 0.25;
    },
    [sizeWeightsMap]
  );

  // Fetch Latest Active Carts List
  const loadActiveCarts = useCallback(async (page = 1, search = "") => {
    setLoadingActiveCarts(true);
    try {
      const data = await fetchAdminCartListApi(page, cartsLimit, search, true);
      setActiveCarts(data.carts || []);
      setCartsTotal(data.total || 0);
      setCartsPage(data.page || 1);
      setCartsTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error("Error loading active carts:", error);
      toast.error("Failed to fetch active carts");
    } finally {
      setLoadingActiveCarts(false);
    }
  }, [cartsLimit]);

  useEffect(() => {
    if (canView && !selectedParent) {
      loadActiveCarts(cartsPage, listSearchTerm);
    }
  }, [canView, selectedParent, cartsPage, listSearchTerm, loadActiveCarts]);

  // Handle direct search for a student / parent
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchTerm.trim()) {
      toast.warn("Please enter a search term");
      return;
    }

    setSearching(true);
    try {
      const filterParam = searchFilterType === "Parent Phone" ? "USID" : searchFilterType;
      const res = await fetchALLStudent(1, 20, searchTerm.trim(), filterParam);
      if (res?.students?.length > 0) {
        setSearchResults(res.students);
      } else {
        setSearchResults([]);
        toast.info("No matching students found");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search students");
    } finally {
      setSearching(false);
    }
  };

  // Select student & open Cart Editor
  const handleSelectStudent = async (student) => {
    setLoading(true);
    setSelectedStudent(student);
    setSearchResults([]);

    try {
      const parentData = await getParentByStudentUsid(student.usid);
      if (parentData) {
        setSelectedParent(parentData);
        await loadCart(parentData.id);
      } else {
        toast.error("Could not find parent record for this student");
      }
    } catch (error) {
      console.error("Error selecting student:", error);
      toast.error("Failed to load user details and cart");
    } finally {
      setLoading(false);
    }
  };

  // Open Cart directly from Active Carts Table row
  const handleOpenCartFromList = (cartItem) => {
    if (cartItem.parent) {
      setSelectedParent(cartItem.parent);
      if (cartItem.students?.length > 0) {
        setSelectedStudent(cartItem.students[0]);
      }
      loadCart(cartItem.parentId);
    } else {
      toast.error("Parent details missing for this cart");
    }
  };

  // Load cart items by parentId
  const loadCart = async (parentId) => {
    setLoadingCart(true);
    try {
      const cartData = await fetchUserCartApi(parentId);
      initializeCartEditingState(cartData);
    } catch (error) {
      if (error?.response?.status === 404 || error?.message?.includes("not found")) {
        setCartItemsState([]);
      } else {
        console.error("Error loading cart:", error);
        toast.error("Failed to load user cart");
      }
    } finally {
      setLoadingCart(false);
    }
  };

  // Transform cart response to editable state
  const initializeCartEditingState = (cartData) => {
    if (!cartData?.items) {
      setCartItemsState([]);
      return;
    }

    const editableItems = cartData.items.map((item) => {
      const isIndividual = item.bundle?.isIndividualProduct;
      const products = (item.products || []).map((cp) => {
        let availableSizes = [];
        const rawSizes = cp.product?.available_sizes || cp.product?.availableSizes;

        if (Array.isArray(rawSizes)) {
          availableSizes = rawSizes;
        } else if (typeof rawSizes === "string") {
          try {
            const parsed = JSON.parse(rawSizes);
            if (Array.isArray(parsed)) availableSizes = parsed;
            else availableSizes = rawSizes.split(",").map((s) => s.trim());
          } catch {
            availableSizes = rawSizes.split(",").map((s) => s.trim());
          }
        }

        availableSizes = availableSizes.map((s) => String(s).trim()).filter(Boolean);

        if (cp.size && !availableSizes.includes(cp.size)) {
          availableSizes = [cp.size, ...availableSizes];
        }

        if (availableSizes.length === 0) {
          availableSizes = cp.size ? [cp.size] : ["Free-NA"];
        }

        const chosenSize = cp.size || (availableSizes[0] || "");
        const pWeight = getProductWeightKg(
          cp.productId,
          chosenSize,
          cp.product?.weight
        );

        return {
          productId: cp.productId,
          productName: cp.product?.name || `Product #${cp.productId}`,
          productSku: cp.product?.sku || "",
          productWeight: pWeight,
          fallbackWeight: cp.product?.weight || 0,
          productImage: cp.product?.imageUrl || cp.product?.image || null,
          size: chosenSize,
          availableSizes: availableSizes,
          baseQuantity: cp.baseQuantity ?? 0,
          addonQuantity: cp.addonQuantity ?? 0,
          totalQuantity:
            cp.totalQuantity !== undefined && cp.totalQuantity !== null
              ? Number(cp.totalQuantity)
              : item.quantity ?? 1,
          unitPrice: parseFloat(cp.unitPrice) || 0,
          bundlePrice: parseFloat(cp.bundle_price) || parseFloat(cp.unitPrice) || 0,
        };
      });

      return {
        bundleId: item.bundleId,
        bundleName: item.bundle?.name || `Bundle #${item.bundleId}`,
        bundleTotal: parseFloat(item.price) || parseFloat(item.bundle?.totalPrice) || 0,
        isIndividualProduct: isIndividual,
        studentId: item.studentId,
        studentName: item.student?.studentName || "Student",
        studentUsid: item.student?.usid || "",
        studentClass: item.student?.class || "",
        quantity: item.quantity ?? 1,
        products: products,
        isSaving: false,
        isModified: false,
      };
    });

    setCartItemsState(editableItems);
  };

  // Inline size update
  const handleSizeChange = (bundleId, productId, newSize) => {
    setCartItemsState((prev) =>
      prev.map((item) => {
        if (item.bundleId === bundleId) {
          return {
            ...item,
            isModified: true,
            products: item.products.map((p) => {
              if (p.productId === productId) {
                const updatedWeight = getProductWeightKg(
                  productId,
                  newSize,
                  p.fallbackWeight || p.productWeight
                );
                return {
                  ...p,
                  size: newSize,
                  productWeight: updatedWeight,
                };
              }
              return p;
            }),
          };
        }
        return item;
      })
    );
  };

  // Inline quantity stepper (allows decrementing down to 0)
  const handleQuantityChange = (bundleId, productId, delta) => {
    setCartItemsState((prev) =>
      prev.map((item) => {
        if (item.bundleId === bundleId) {
          const updatedProducts = item.products.map((p) => {
            if (p.productId === productId) {
              const currentTotal = Number(p.totalQuantity) || 0;
              const newTotal = Math.max(0, currentTotal + delta);
              const newAddon = Math.max(0, newTotal - (p.baseQuantity || 0));
              return {
                ...p,
                totalQuantity: newTotal,
                addonQuantity: newAddon,
              };
            }
            return p;
          });

          const newBundleQty = item.isIndividualProduct
            ? (updatedProducts[0]?.totalQuantity ?? item.quantity)
            : item.quantity;

          return {
            ...item,
            isModified: true,
            quantity: newBundleQty,
            products: updatedProducts,
          };
        }
        return item;
      })
    );
  };

  // Inline quantity direct input (allows 0 and empty input while typing)
  const handleQuantityInput = (bundleId, productId, value) => {
    if (value === "") {
      setCartItemsState((prev) =>
        prev.map((item) => {
          if (item.bundleId === bundleId) {
            const updatedProducts = item.products.map((p) => {
              if (p.productId === productId) {
                return {
                  ...p,
                  totalQuantity: "",
                  addonQuantity: 0,
                };
              }
              return p;
            });
            const newBundleQty = item.isIndividualProduct
              ? 0
              : item.quantity;
            return {
              ...item,
              isModified: true,
              quantity: newBundleQty,
              products: updatedProducts,
            };
          }
          return item;
        })
      );
      return;
    }

    const num = parseInt(value, 10);
    const validNum = isNaN(num) || num < 0 ? 0 : num;

    setCartItemsState((prev) =>
      prev.map((item) => {
        if (item.bundleId === bundleId) {
          const updatedProducts = item.products.map((p) => {
            if (p.productId === productId) {
              return {
                ...p,
                totalQuantity: validNum,
                addonQuantity: Math.max(0, validNum - (p.baseQuantity || 0)),
              };
            }
            return p;
          });
          const newBundleQty = item.isIndividualProduct
            ? (updatedProducts[0]?.totalQuantity ?? item.quantity)
            : item.quantity;
          return {
            ...item,
            isModified: true,
            quantity: newBundleQty,
            products: updatedProducts,
          };
        }
        return item;
      })
    );
  };

  // Reset Addons for a product back to base quantity
  const handleResetAddon = (bundleId, productId) => {
    setCartItemsState((prev) =>
      prev.map((item) => {
        if (item.bundleId === bundleId) {
          const updatedProducts = item.products.map((p) =>
            p.productId === productId
              ? { ...p, totalQuantity: Math.max(0, p.baseQuantity || 0), addonQuantity: 0 }
              : p
          );
          const newBundleQty = item.isIndividualProduct
            ? (updatedProducts[0]?.totalQuantity ?? item.quantity)
            : item.quantity;
          return {
            ...item,
            isModified: true,
            quantity: newBundleQty,
            products: updatedProducts,
          };
        }
        return item;
      })
    );
  };

  // Remove a product row from a bundle
  const handleRemoveProductFromBundle = (bundleId, productId) => {
    setCartItemsState((prev) =>
      prev.map((item) => {
        if (item.bundleId === bundleId) {
          if (item.products.length <= 1) {
            toast.warn("A cart item must have at least one product. To remove the whole item, use the delete button.");
            return item;
          }
          return {
            ...item,
            isModified: true,
            products: item.products.filter((p) => p.productId !== productId),
          };
        }
        return item;
      })
    );
  };

  // Open "Add Product to Bundle" modal
  const handleOpenAddToBundleModal = (bundleItem) => {
    setTargetBundleItem(bundleItem);
    setSelectedExtraProduct(null);
    setSelectedExtraSize("");
    setSelectedExtraQty(1);
    setExtraProductSearch("");
    setShowAddToBundleModal(true);
  };

  // Confirm adding extra product into the bundle
  const handleConfirmAddProductToBundle = () => {
    if (!targetBundleItem || !selectedExtraProduct) {
      toast.warn("Please select a product to add");
      return;
    }

    const rawSizes = selectedExtraProduct.available_sizes || selectedExtraProduct.availableSizes || ["Free-NA"];
    const sizes = Array.isArray(rawSizes) ? rawSizes : [rawSizes];
    const finalSize = selectedExtraSize || sizes[0] || "Free-NA";
    const qty = Math.max(1, selectedExtraQty || 1);

    setCartItemsState((prev) =>
      prev.map((item) => {
        if (item.bundleId === targetBundleItem.bundleId) {
          const existingIdx = item.products.findIndex((p) => p.productId === selectedExtraProduct.product_id);
          if (existingIdx >= 0) {
            const updatedProducts = item.products.map((p, idx) =>
              idx === existingIdx
                ? {
                    ...p,
                    size: finalSize,
                    totalQuantity: p.totalQuantity + qty,
                    addonQuantity: p.addonQuantity + qty,
                  }
                : p
            );
            return {
              ...item,
              isModified: true,
              products: updatedProducts,
            };
          }

          const newProd = {
            productId: selectedExtraProduct.product_id,
            productName: selectedExtraProduct.name || selectedExtraProduct.product_name,
            productSku: selectedExtraProduct.sku || "",
            productWeight: getProductWeightKg(
              selectedExtraProduct.product_id,
              finalSize,
              selectedExtraProduct.weight
            ),
            fallbackWeight: selectedExtraProduct.weight || 0,
            productImage: selectedExtraProduct.imageUrl || selectedExtraProduct.image || null,
            size: finalSize,
            availableSizes: sizes,
            baseQuantity: 0,
            addonQuantity: qty,
            totalQuantity: qty,
            unitPrice: selectedExtraProduct.unit_price || 0,
            bundlePrice: selectedExtraProduct.bundle_price || selectedExtraProduct.unit_price || 0,
          };

          return {
            ...item,
            isModified: true,
            products: [...item.products, newProd],
          };
        }
        return item;
      })
    );

    toast.info(`Added "${selectedExtraProduct.name || selectedExtraProduct.product_name}" to bundle. Click "Save Changes" on the bundle to apply.`);
    setShowAddToBundleModal(false);
  };

  // Save changes for specific bundle item
  const handleSaveItemChanges = async (bundleItem) => {
    if (!selectedParent?.id) return;

    for (const p of bundleItem.products) {
      if (!p.size && p.totalQuantity > 0) {
        toast.error(`Please select a size for ${p.productName}`);
        return;
      }
    }

    setCartItemsState((prev) =>
      prev.map((item) =>
        item.bundleId === bundleItem.bundleId ? { ...item, isSaving: true } : item
      )
    );

    try {
      const payload = {
        parentId: selectedParent.id,
        bundleId: bundleItem.bundleId,
        studentId: bundleItem.studentId,
        quantity: bundleItem.quantity,
        cartItemProducts: bundleItem.products.map((p) => ({
          product_id: p.productId,
          size: p.size,
          base_quantity: p.baseQuantity,
          addon_quantity: p.addonQuantity,
          total_quantity: p.totalQuantity,
          unit_price: p.unitPrice,
          bundle_price: p.bundlePrice,
        })),
      };

      await updateUserCartBundleApi(payload);
      toast.success(`Updated "${bundleItem.bundleName}" successfully!`);
      await loadCart(selectedParent.id);
    } catch (error) {
      console.error("Save cart item error:", error);
      toast.error(error?.response?.data?.message || "Failed to update cart item");
    } finally {
      setCartItemsState((prev) =>
        prev.map((item) =>
          item.bundleId === bundleItem.bundleId ? { ...item, isSaving: false, isModified: false } : item
        )
      );
    }
  };

  // Open confirm single item removal
  const confirmDeleteItem = (item) => {
    setItemToDelete(item);
    setIsClearingAll(false);
    setParentToClear(selectedParent?.id);
    setShowConfirmDelete(true);
  };

  // Open confirm clear all
  const confirmClearAll = (parentId = selectedParent?.id) => {
    setItemToDelete(null);
    setIsClearingAll(true);
    setParentToClear(parentId);
    setShowConfirmDelete(true);
  };

  // Execute deletion / clearing
  const handleExecuteDelete = async () => {
    if (!parentToClear) return;
    setShowConfirmDelete(false);
    setLoading(true);

    try {
      if (isClearingAll) {
        await clearAdminUserCartApi(parentToClear);
        toast.success("User cart has been cleared");
      } else if (itemToDelete) {
        await deleteUserCartBundleApi({
          parentId: parentToClear,
          bundleId: itemToDelete.bundleId,
        });
        toast.success(`Removed "${itemToDelete.bundleName}" from cart`);
      }

      if (selectedParent) {
        await loadCart(selectedParent.id);
      } else {
        await loadActiveCarts(cartsPage, listSearchTerm);
      }
    } catch (error) {
      console.error("Delete cart item error:", error);
      toast.error(error?.response?.data?.message || "Failed to remove item");
    } finally {
      setLoading(false);
      setItemToDelete(null);
      setParentToClear(null);
    }
  };

  // Helper to calculate weight slab cost
  const calculateSlabShipping = (weightKg) => {
    const slabs = (Array.isArray(settings?.weightSlabs) ? settings.weightSlabs : [])
      .map((s) => ({
        minWeight: Number(s.minWeight) || 0,
        maxWeight: Number(s.maxWeight) || 0,
        cost: Number(s.cost) || 0,
      }))
      .filter((s) => s.maxWeight > 0)
      .sort((a, b) => a.maxWeight - b.maxWeight);

    const w = Math.max(0, Number(weightKg) || 0);

    if (slabs.length > 0) {
      const matchedSlab = slabs.find((s) => w <= s.maxWeight);
      if (matchedSlab) {
        return {
          cost: Number(matchedSlab.cost) || 0,
          details: `${w.toFixed(2)} kg (Slab up to ${matchedSlab.maxWeight} kg: ₹${matchedSlab.cost})`,
          slab: matchedSlab,
        };
      }
      const topSlab = slabs[slabs.length - 1];
      const baseCost = Number(topSlab.cost) || 0;
      const perKgRate = Number(settings?.perKgRate || settings?.additionalWeightAmount) || 80;
      const extraWeight = Math.max(0, w - topSlab.maxWeight);
      const fee = baseCost + Math.ceil(extraWeight) * perKgRate;
      return {
        cost: fee,
        details: `${w.toFixed(2)} kg (${topSlab.maxWeight} kg @ ₹${baseCost} + ${Math.ceil(extraWeight)} kg extra @ ₹${perKgRate}/kg)`,
        slab: topSlab,
      };
    }

    const perKgRate = Number(settings?.perKgRate || settings?.additionalWeightAmount) || 80;
    const fee = Math.max(105, Math.ceil(w || 1) * perKgRate);
    return {
      cost: fee,
      details: `${w.toFixed(2)} kg @ ₹${perKgRate}/kg (min ₹${fee})`,
      slab: null,
    };
  };

  // Calculate live Cart Summary Totals
  const calculateTotals = () => {
    let totalUnits = 0;
    let totalPrice = 0;
    let totalWeight = 0;
    let bundleCount = 0;
    let singleItemCount = 0;
    let singleWeightGrams = 0;
    let singleTotalPrice = 0;
    let bundleTotalPrice = 0;

    cartItemsState.forEach((item) => {
      let itemWeight = 0;
      let itemPrice = 0;
      let itemActiveUnits = 0;

      item.products.forEach((p) => {
        const qty = Number(p.totalQuantity) || 0;
        itemActiveUnits += qty;
        totalUnits += qty;

        const weightKg = getProductWeightKg(p.productId, p.size, p.productWeight);
        const weightGrams = Math.round(weightKg * 1000);
        const pTotalGrams = qty * weightGrams;

        totalWeight += pTotalGrams;
        itemWeight += pTotalGrams;

        const uPrice = item.isIndividualProduct
          ? Number(p.unitPrice) || 0
          : Number(p.bundlePrice || p.unitPrice) || 0;
        const lineTotal = qty * uPrice;
        totalPrice += lineTotal;
        itemPrice += lineTotal;
      });

      // Only count items with positive active units for delivery charges
      if (itemActiveUnits > 0) {
        if (item.isIndividualProduct) {
          singleItemCount += 1;
          singleWeightGrams += itemWeight;
          singleTotalPrice += itemPrice;
        } else {
          bundleCount += 1;
          bundleTotalPrice += itemPrice;
        }
      }
    });

    const totalWeightKg = totalWeight > 0 ? totalWeight / 1000 : 0;
    const singleWeightKg = singleWeightGrams > 0 ? singleWeightGrams / 1000 : 0;

    const isDeliveryActive =
      settings?.enableHomeDelivery !== false &&
      (settings?.enableHomeDelivery || settings?.isCourierFeeApplicable || (Array.isArray(settings?.weightSlabs) && settings.weightSlabs.length > 0));

    let shippingCost = 0;
    let bundleShipping = 0;
    let singleShipping = 0;
    let bundleDeliveryDetails = "";
    let singleDeliveryDetails = "";
    let deliveryDetails = "";

    const homePrice = Number(settings?.homeDeliveryPrice) || 0;
    const additionalPrice = Number(settings?.additionalBundlePrice) || 0;

    if (isDeliveryActive) {
      // 1. Calculate Bundle Delivery
      if (bundleCount > 0) {
        if (bundleCount === 1) {
          bundleShipping = homePrice;
          bundleDeliveryDetails = `1 Bundle @ ₹${homePrice}`;
        } else if (additionalPrice > 0) {
          bundleShipping = homePrice + (bundleCount - 1) * additionalPrice;
          bundleDeliveryDetails = `1st Bundle ₹${homePrice} + ${bundleCount - 1} Additional @ ₹${additionalPrice}`;
        } else {
          bundleShipping = bundleCount * homePrice;
          bundleDeliveryDetails = `${bundleCount} Bundles @ ₹${homePrice}`;
        }
      }

      // 2. Calculate Single Product (Individual Items) Delivery
      if (singleItemCount > 0) {
        const slabCalc = calculateSlabShipping(singleWeightKg);
        singleShipping = slabCalc.cost;
        singleDeliveryDetails = singleWeightKg > 0
          ? `${singleItemCount} Item${singleItemCount > 1 ? "s" : ""} (${slabCalc.details})`
          : `${singleItemCount} Item${singleItemCount > 1 ? "s" : ""} (Standard Slab @ ₹${singleShipping})`;
      }

      // 3. Combined Delivery
      shippingCost = bundleShipping + singleShipping;

      if (bundleCount > 0 && singleItemCount > 0) {
        deliveryDetails = `${bundleDeliveryDetails} + Individual Items (@ ₹${singleShipping})`;
      } else if (bundleCount > 0) {
        deliveryDetails = bundleDeliveryDetails;
      } else if (singleItemCount > 0) {
        deliveryDetails = singleDeliveryDetails;
      }
    }

    return {
      totalUnits,
      bundleCount,
      singleItemCount,
      homePrice,
      additionalPrice,
      totalWeightGrams: totalWeight,
      totalWeight: Number(totalWeightKg.toFixed(2)),
      singleWeightKg: Number(singleWeightKg.toFixed(2)),
      totalPrice: Number(totalPrice.toFixed(2)),
      bundleTotalPrice: Number(bundleTotalPrice.toFixed(2)),
      singleTotalPrice: Number(singleTotalPrice.toFixed(2)),
      bundleShipping: Number(bundleShipping.toFixed(2)),
      singleShipping: Number(singleShipping.toFixed(2)),
      shippingCost: Number(shippingCost.toFixed(2)),
      grandTotal: Number((totalPrice + shippingCost).toFixed(2)),
      isDeliveryActive,
      deliveryDetails,
      bundleDeliveryDetails,
      singleDeliveryDetails,
    };
  };

  // Helper to calculate delivery info for a specific card item
  const getItemDeliveryInfo = (item, idx) => {
    if (!totals.isDeliveryActive) {
      return { fee: 0, label: "Delivery Free / Inactive", isDeliveryActive: false };
    }

    const itemActiveUnits = (item.products || []).reduce(
      (sum, p) => sum + (Number(p.totalQuantity) || 0),
      0
    );
    if (itemActiveUnits === 0) {
      return { fee: 0, label: "Quantity is 0", isDeliveryActive: false };
    }

    if (!item.isIndividualProduct) {
      const priorBundles = cartItemsState.slice(0, idx).filter(
        (it) =>
          !it.isIndividualProduct &&
          (it.products || []).some((p) => (Number(p.totalQuantity) || 0) > 0)
      ).length;
      const isFirst = priorBundles === 0;
      const fee = isFirst
        ? totals.homePrice
        : totals.additionalPrice > 0
        ? totals.additionalPrice
        : totals.homePrice;
      const label = isFirst
        ? `1st Bundle Delivery (@ ₹${totals.homePrice})`
        : `Additional Bundle Delivery (@ ₹${totals.additionalPrice > 0 ? totals.additionalPrice : totals.homePrice})`;
      return { fee, label, isDeliveryActive: true };
    } else {
      if (totals.singleItemCount === 1) {
        return {
          fee: totals.singleShipping,
          label: totals.singleWeightKg > 0
            ? `Single Item Delivery (${totals.singleWeightKg} kg · ₹${totals.singleShipping})`
            : `Single Item Delivery (Slab: ₹${totals.singleShipping})`,
          isDeliveryActive: true,
        };
      }

      const itemWeightGrams = item.products.reduce((acc, p) => {
        const qty = Number(p.totalQuantity) || 0;
        const weightKg = getProductWeightKg(p.productId, p.size, p.productWeight);
        const wG = Math.round(weightKg * 1000);
        return acc + qty * wG;
      }, 0);
      const itemWeightKg = itemWeightGrams > 0 ? itemWeightGrams / 1000 : 0;

      let allocatedFee = totals.singleShipping;
      if (totals.singleWeightKg > 0) {
        allocatedFee = Number(((itemWeightKg / totals.singleWeightKg) * totals.singleShipping).toFixed(2));
      } else {
        allocatedFee = Number((totals.singleShipping / totals.singleItemCount).toFixed(2));
      }

      return {
        fee: allocatedFee,
        label: itemWeightKg > 0
          ? `Individual Item Share (${itemWeightKg.toFixed(2)} kg · Total Single Delivery: ₹${totals.singleShipping})`
          : `Individual Item Share (Total Single Delivery: ₹${totals.singleShipping})`,
        isDeliveryActive: true,
      };
    }
  };

  // Helper to calculate shipping & grand total for any cart in list view
  const calculateCartShippingAndTotal = (cartItem) => {
    const itemsSubtotal = Number(cartItem?.totalPrice || 0);
    let totalWeightGrams = 0;
    let singleWeightGrams = 0;
    let bundleCount = 0;
    let singleItemCount = 0;

    (cartItem?.items || []).forEach((item) => {
      let itemWeightGrams = 0;
      let itemQty = 0;

      (item.products || []).forEach((cp) => {
        const qty =
          cp.totalQuantity !== undefined && cp.totalQuantity !== null
            ? Number(cp.totalQuantity)
            : Number(item.quantity) || 1;
        itemQty += qty;
        const weightKg = getProductWeightKg(
          cp.productId || cp.product_id || cp.product?.id,
          cp.size,
          cp.product?.weight
        );
        const weightInGrams = Math.round(weightKg * 1000);
        const pWeightGrams = qty * weightInGrams;
        totalWeightGrams += pWeightGrams;
        itemWeightGrams += pWeightGrams;
      });

      if ((item.products || []).length === 0) {
        const fallbackQty = Number(item.quantity) || 1;
        itemQty += fallbackQty;
        const uniformGrams = 250;
        const pWeightGrams = fallbackQty * uniformGrams;
        totalWeightGrams += pWeightGrams;
        itemWeightGrams += pWeightGrams;
      }

      if (itemQty > 0) {
        if (item.bundle?.isIndividualProduct) {
          singleItemCount += 1;
          singleWeightGrams += itemWeightGrams;
        } else {
          bundleCount += 1;
        }
      }
    });

    const weightKg = totalWeightGrams > 0 ? totalWeightGrams / 1000 : 0;
    const singleWeightKg = singleWeightGrams > 0 ? singleWeightGrams / 1000 : 0;

    const isDeliveryActive =
      settings?.enableHomeDelivery !== false &&
      (settings?.enableHomeDelivery || settings?.isCourierFeeApplicable || (Array.isArray(settings?.weightSlabs) && settings.weightSlabs.length > 0));

    let shippingFee = 0;
    let bundleShipping = 0;
    let singleShipping = 0;
    let deliveryDetails = "";

    const homePrice = Number(settings?.homeDeliveryPrice) || 0;
    const additionalPrice = Number(settings?.additionalBundlePrice) || 0;

    if (isDeliveryActive) {
      if (bundleCount > 0) {
        if (bundleCount === 1) {
          bundleShipping = homePrice;
        } else if (additionalPrice > 0) {
          bundleShipping = homePrice + (bundleCount - 1) * additionalPrice;
        } else {
          bundleShipping = bundleCount * homePrice;
        }
      }

      if (singleItemCount > 0) {
        const slabCalc = calculateSlabShipping(singleWeightKg);
        singleShipping = slabCalc.cost;
      }

      shippingFee = bundleShipping + singleShipping;

      if (bundleCount > 0 && singleItemCount > 0) {
        deliveryDetails = `${bundleCount} Bundle${bundleCount > 1 ? "s" : ""} (@ ₹${bundleShipping}) + Individual Items (@ ₹${singleShipping})`;
      } else if (bundleCount > 0) {
        deliveryDetails = bundleCount === 1 ? `1 Bundle @ ₹${homePrice}` : `${bundleCount} Bundles @ ₹${bundleShipping}`;
      } else if (singleItemCount > 0) {
        deliveryDetails = `${singleItemCount} Single Item${singleItemCount > 1 ? "s" : ""} @ ₹${singleShipping}`;
      }
    }

    return {
      itemsSubtotal,
      bundleCount,
      singleItemCount,
      deliveryDetails,
      weightKg: Number(weightKg.toFixed(2)),
      weightGrams: totalWeightGrams,
      singleWeightKg: Number(singleWeightKg.toFixed(2)),
      shippingFee: Number(shippingFee.toFixed(2)),
      bundleShipping: Number(bundleShipping.toFixed(2)),
      singleShipping: Number(singleShipping.toFixed(2)),
      finalPrice: Number((itemsSubtotal + shippingFee).toFixed(2)),
      isDeliveryActive,
    };
  };

  const totals = calculateTotals();

  // Aggregate KPI Stats across active carts including delivery
  const totalCartsGrandTotal = activeCarts.reduce((acc, c) => {
    const calc = calculateCartShippingAndTotal(c);
    return acc + calc.finalPrice;
  }, 0);

  const kpiStats = {
    totalCarts: cartsTotal,
    totalValue: totalCartsGrandTotal,
    totalUnits: activeCarts.reduce((acc, c) => acc + (c.totalUnits || 0), 0),
    avgCartValue: cartsTotal > 0 ? (totalCartsGrandTotal / (activeCarts.length || 1)) : 0,
  };

  // Helper to normalize catalog item data (bundles or single items)
  const transformCatalogItem = (item, isAssigned = false) => {
    const isInd = Boolean(item.isIndividualProduct ?? item.is_individual_product);
    const rawProducts = item.bundleProducts || item.products || [];
    const products = rawProducts.map((bp) => {
      const p = bp.product || bp;
      let rawSizes = p.available_sizes || p.availableSizes || bp.available_sizes || bp.availableSizes;
      let parsedSizes = [];
      if (Array.isArray(rawSizes)) {
        parsedSizes = rawSizes;
      } else if (typeof rawSizes === "string") {
        try {
          const parsed = JSON.parse(rawSizes);
          parsedSizes = Array.isArray(parsed) ? parsed : rawSizes.split(",").map((s) => s.trim());
        } catch {
          parsedSizes = rawSizes.split(",").map((s) => s.trim());
        }
      }
      parsedSizes = parsedSizes.map((s) => String(s).trim()).filter(Boolean);
      if (parsedSizes.length === 0) parsedSizes = ["Free-NA"];

      return {
        product_id: p.id || bp.productId || bp.product_id,
        name: p.display_name || p.name || bp.name || bp.product_name || `Product #${p.id || bp.productId || bp.product_id}`,
        product_name: p.name || bp.product_name || p.display_name,
        display_name: p.display_name || p.name,
        unit_price: parseFloat(p.unitPrice ?? p.unit_price ?? bp.unit_price ?? 0),
        bundle_price: parseFloat(p.bundle_price ?? bp.bundle_price ?? p.unitPrice ?? bp.unit_price ?? 0),
        quantity: bp.quantity || 1,
        available_sizes: parsedSizes,
        size: bp.size || bp.product_size || parsedSizes[0],
        product_size: bp.product_size || bp.size || parsedSizes[0],
        weight: parseFloat(p.weight || bp.weight || 0),
        sku: p.sku || bp.sku || "",
        imageUrl: p.imageUrl || p.image || bp.image || null,
      };
    });

    const price = parseFloat(item.totalPrice ?? item.bundle_total ?? item.price ?? 0);

    return {
      bundle_id: item.id || item.bundle_id,
      id: item.id || item.bundle_id,
      bundle_name: item.display_name || item.name || item.bundle_name,
      name: item.name || item.bundle_name || item.display_name,
      display_name: item.display_name || item.name,
      bundle_total: price,
      totalPrice: price,
      price: price,
      isIndividualProduct: isInd,
      gender: item.gender || "",
      academic_year: item.academicYear || item.academic_year || "",
      applicableClasses: item.applicableClasses || item.applicable_classes || "",
      applicableStudentTypes: item.applicableStudentTypes || item.studentType || "",
      isAssigned,
      products,
    };
  };

  // Open "Add Item to Cart" modal
  const handleOpenAddModal = async (studentToUse, defaultMode = "bundle") => {
    const activeStudent = studentToUse || selectedStudent;
    if (!activeStudent) {
      toast.warn("Please select a student first");
      return;
    }

    setModalStudent(activeStudent);
    setShowAddModal(true);
    setSelectedItemToAdd(null);
    setModalProductSizes({});
    setModalProductQuantities({});
    setModalSearchTerm("");
    setItemSourceFilter("all");
    setModalMode(defaultMode);

    setFetchingCatalog(true);
    try {
      const [assignedBundlesRes, assignedSinglesRes, allBundlesRes] = await Promise.all([
        fetchStudentBundlesApi(activeStudent.usid, false).catch(() => []),
        fetchStudentBundlesApi(activeStudent.usid, true).catch(() => []),
        fetchAllBundles().catch(() => []),
      ]);

      const assignedBList = (Array.isArray(assignedBundlesRes) ? assignedBundlesRes : (assignedBundlesRes ? [assignedBundlesRes] : []))
        .map((b) => transformCatalogItem(b, true));

      const assignedSList = (Array.isArray(assignedSinglesRes) ? assignedSinglesRes : (assignedSinglesRes ? [assignedSinglesRes] : []))
        .map((b) => transformCatalogItem(b, true));

      let allBList = [];
      let allSList = [];

      if (Array.isArray(allBundlesRes)) {
        allBundlesRes.forEach((b) => {
          const isInd = Boolean(b.isIndividualProduct);
          if (isInd) {
            const isAssigned = assignedSList.some((ab) => (ab.bundle_id || ab.id) === (b.id || b.bundle_id));
            allSList.push(transformCatalogItem(b, isAssigned));
          } else {
            const isAssigned = assignedBList.some((ab) => (ab.bundle_id || ab.id) === (b.id || b.bundle_id));
            allBList.push(transformCatalogItem(b, isAssigned));
          }
        });
      }

      if (allBList.length === 0) allBList = assignedBList;
      if (allSList.length === 0) allSList = assignedSList;

      setAssignedBundles(assignedBList);
      setAvailableBundles(allBList);
      setAssignedSingleItems(assignedSList);
      setAvailableSingleItems(allSList);

      // Collect product list for the "Add Product to Bundle" feature
      const productMap = new Map();
      [...allSList, ...allBList].forEach((bundle) => {
        (bundle.products || []).forEach((p) => {
          if (!productMap.has(p.product_id)) {
            productMap.set(p.product_id, p);
          }
        });
      });
      setExtraProductList(Array.from(productMap.values()));
    } catch (error) {
      console.error("Error fetching catalog for student:", error);
      toast.error("Failed to load catalog items");
    } finally {
      setFetchingCatalog(false);
    }
  };

  // Select bundle/item to add inside modal
  const handleSelectItemToAdd = (item) => {
    setSelectedItemToAdd(item);
    const initialSizes = {};
    const initialQuantities = {};

    (item.products || []).forEach((p) => {
      let sizes = [];
      const rawSizes = p.available_sizes || p.availableSizes;
      if (Array.isArray(rawSizes)) {
        sizes = rawSizes;
      } else if (typeof rawSizes === "string") {
        try {
          const parsed = JSON.parse(rawSizes);
          sizes = Array.isArray(parsed) ? parsed : rawSizes.split(",").map((s) => s.trim());
        } catch {
          sizes = rawSizes.split(",").map((s) => s.trim());
        }
      }
      sizes = sizes.map((s) => String(s).trim()).filter(Boolean);
      if (p.size && !sizes.includes(p.size)) sizes = [p.size, ...sizes];
      if (p.product_size && !sizes.includes(p.product_size)) sizes = [p.product_size, ...sizes];
      if (sizes.length === 0) sizes = ["Free-NA"];

      initialSizes[p.product_id] = p.product_size || p.size || sizes[0] || "Free-NA";
      initialQuantities[p.product_id] = p.quantity || 1;
    });

    setModalProductSizes(initialSizes);
    setModalProductQuantities(initialQuantities);
  };

  // Filter items for modal based on mode and search
  const filteredModalItems = useMemo(() => {
    let list = [];
    if (modalMode === "bundle") {
      list = itemSourceFilter === "assigned" ? assignedBundles : availableBundles;
    } else {
      list = itemSourceFilter === "assigned" ? assignedSingleItems : availableSingleItems;
    }

    if (!modalSearchTerm.trim()) return list;

    const term = modalSearchTerm.toLowerCase().trim();
    return list.filter((item) => {
      const name = (item.bundle_name || item.name || "").toLowerCase();
      const classes = (item.applicableClasses || "").toLowerCase();
      const gender = (item.gender || "").toLowerCase();
      const studentType = (item.applicableStudentTypes || "").toLowerCase();
      const prodNames = (item.products || []).map((p) => `${p.name} ${p.sku}`).join(" ").toLowerCase();
      return name.includes(term) || classes.includes(term) || gender.includes(term) || studentType.includes(term) || prodNames.includes(term);
    });
  }, [modalMode, itemSourceFilter, assignedBundles, availableBundles, assignedSingleItems, availableSingleItems, modalSearchTerm]);

  // Execute Add to Cart from Modal
  const handleExecuteAddToCart = async () => {
    if (!selectedItemToAdd || !modalStudent || !selectedParent) return;

    for (const p of selectedItemToAdd.products || []) {
      const size = modalProductSizes[p.product_id];
      if (!size) {
        toast.error(`Please select a size for ${p.name || p.product_name}`);
        return;
      }
    }

    setAddingItem(true);
    try {
      const isInd = selectedItemToAdd.isIndividualProduct;
      const cartItemProducts = (selectedItemToAdd.products || []).map((p) => {
        const qty = modalProductQuantities[p.product_id] || p.quantity || 1;
        const baseQty = isInd ? 0 : (p.quantity ?? 1);
        return {
          product_id: p.product_id,
          size: modalProductSizes[p.product_id] || "Free-NA",
          base_quantity: baseQty,
          addon_quantity: Math.max(0, qty - baseQty),
          total_quantity: qty,
          unit_price: parseFloat(p.unit_price) || 0,
          bundle_price: parseFloat(p.bundle_price) || parseFloat(p.unit_price) || 0,
        };
      });

      const payload = {
        parentId: selectedParent.id,
        bundleId: selectedItemToAdd.bundle_id || selectedItemToAdd.id,
        studentId: modalStudent.id,
        quantity: isInd ? (cartItemProducts[0]?.total_quantity || 1) : 1,
        cartItemProducts,
        isAdmin: true,
      };

      await updateUserCartBundleApi(payload);
      toast.success(`Added "${selectedItemToAdd.bundle_name || selectedItemToAdd.name}" to user's cart!`);
      setShowAddModal(false);
      await loadCart(selectedParent.id);
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error(error?.response?.data?.message || "Failed to add item to cart");
    } finally {
      setAddingItem(false);
    }
  };

  if (!canView) {
    return (
      <div className="cart-management-container">
        <Alert variant="danger" className="d-flex align-items-center gap-3">
          <AlertCircle size={24} />
          <div>
            <h5 className="mb-1">Access Restricted</h5>
            <p className="mb-0">You do not have permission to view or manage user carts.</p>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="cart-management-container">
      <FullPageSpinner loading={loading} />

      {/* Breadcrumb Navigation */}
      <div className="cart-breadcrumb">
        <span
          className={`cart-breadcrumb-item ${!selectedParent ? "active" : ""}`}
          onClick={() => {
            if (selectedParent) {
              setSelectedParent(null);
              setSelectedStudent(null);
              setCartItemsState([]);
            }
          }}
        >
          <ShoppingCart size={15} /> Cart Management
        </span>
        {selectedParent && (
          <>
            <ChevronRight size={14} className="text-muted" />
            <span className="cart-breadcrumb-item active">
              Parent #{selectedParent.id} ({selectedParent.parentName || "User"})
            </span>
          </>
        )}
      </div>

      {/* Header Banner */}
      <div className="cart-header-card">
        <div className="cart-header-title-group">
          <div className="cart-header-icon-box">
            <ShoppingCart size={24} />
          </div>
          <div>
            <h1 className="cart-header-title">
              {selectedParent ? "Manage User Cart" : "User Cart Management"}
            </h1>
            <p className="cart-header-subtitle">
              {selectedParent
                ? `Edit uniform items, product sizes & quantities for Parent ID #${selectedParent.id}`
                : "Real-time oversight and administration for active parent & student uniform shopping carts."}
            </p>
          </div>
        </div>

        {selectedParent ? (
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-inline-flex align-items-center gap-1 px-3 py-2 fw-semibold"
              onClick={() => {
                setSelectedParent(null);
                setSelectedStudent(null);
                setCartItemsState([]);
              }}
            >
              <ArrowLeft size={16} /> Back to All Carts
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              className="d-inline-flex align-items-center gap-1 px-3 py-2 fw-semibold"
              onClick={() => loadCart(selectedParent.id)}
              disabled={loadingCart}
            >
              <RefreshCw size={15} className={loadingCart ? "spin" : ""} />
              Refresh
            </Button>
            {canEdit && (
              <Button
                variant="primary"
                size="sm"
                className="d-inline-flex align-items-center gap-1 px-3 py-2 fw-semibold shadow-sm"
                onClick={() => handleOpenAddModal(selectedStudent)}
              >
                <Plus size={16} /> Add Item
              </Button>
            )}
          </div>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              className="d-inline-flex align-items-center gap-1 px-3 py-2 fw-semibold"
              onClick={() => loadActiveCarts(cartsPage, listSearchTerm)}
              disabled={loadingActiveCarts}
            >
              <RefreshCw size={15} className={loadingActiveCarts ? "spin" : ""} />
              Refresh Carts
            </Button>
          </div>
        )}
      </div>

      {/* KPI Stats Overview Cards (List Mode) */}
      {!selectedParent && (
        <Row className="g-3 cart-kpi-row">
          <Col xs={12} sm={6} lg={3}>
            <div className="cart-kpi-card">
              <div className="cart-kpi-icon-wrap kpi-icon-blue">
                <ShoppingCart size={24} />
              </div>
              <div>
                <div className="cart-kpi-title">Active Carts</div>
                <div className="cart-kpi-value">{kpiStats.totalCarts}</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} lg={3}>
            <div className="cart-kpi-card">
              <div className="cart-kpi-icon-wrap kpi-icon-green">
                <DollarSign size={24} />
              </div>
              <div>
                <div className="cart-kpi-title">Pending Value (10 Carts)</div>
                <div className="cart-kpi-value">
                  ₹{kpiStats.totalValue.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} lg={3}>
            <div className="cart-kpi-card">
              <div className="cart-kpi-icon-wrap kpi-icon-amber">
                <Layers size={24} />
              </div>
              <div>
                <div className="cart-kpi-title">Units in Carts</div>
                <div className="cart-kpi-value">{kpiStats.totalUnits} Units</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} lg={3}>
            <div className="cart-kpi-card">
              <div className="cart-kpi-icon-wrap kpi-icon-purple">
                <Users size={24} />
              </div>
              <div>
                <div className="cart-kpi-title">Avg Cart Value</div>
                <div className="cart-kpi-value">₹{kpiStats.avgCartValue.toFixed(2)}</div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Student / Parent Search Filter Panel */}
      <div className="cart-filter-panel">
        <Form onSubmit={handleSearch}>
          <Row className="g-2 align-items-center">
            <Col xs={12} md={3}>
              <Form.Select
                value={searchFilterType}
                onChange={(e) => setSearchFilterType(e.target.value)}
                size="sm"
                className="fw-medium"
              >
                <option value="USID">Lookup by Student USID</option>
                <option value="Name">Lookup by Student Name</option>
                <option value="Class">Lookup by Class</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={selectedParent ? 6 : 9}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0 text-muted">
                  <Search size={15} />
                </span>
                <Form.Control
                  type="text"
                  className="border-start-0 border-end-0"
                  placeholder={`Search student by ${searchFilterType} to open their cart directly...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    className="border-start-0 border-end-0"
                    onClick={() => setSearchTerm("")}
                  >
                    <X size={15} />
                  </Button>
                )}
                <Button variant="primary" type="submit" disabled={searching} className="fw-semibold px-3">
                  {searching ? <Spinner size="sm" animation="border" /> : "Search & Open"}
                </Button>
              </div>
            </Col>
            {selectedParent && (
              <Col xs={12} md={3} className="text-md-end">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="w-100 w-md-auto d-inline-flex align-items-center justify-content-center gap-1"
                  onClick={() => {
                    setSelectedParent(null);
                    setSelectedStudent(null);
                    setCartItemsState([]);
                    setSearchTerm("");
                  }}
                >
                  <X size={14} /> Exit Cart Editor
                </Button>
              </Col>
            )}
          </Row>
        </Form>

        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div className="cart-search-results">
            <div className="p-2 bg-light border-bottom d-flex justify-content-between align-items-center">
              <span className="small font-weight-bold text-muted px-2">
                Search Results ({searchResults.length} students found)
              </span>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-muted text-decoration-none"
                onClick={() => setSearchResults([])}
              >
                Close ✕
              </Button>
            </div>
            <div style={{ maxHeight: "240px", overflowY: "auto" }}>
              {searchResults.map((std) => (
                <div
                  key={std.id}
                  className="search-student-row"
                  onClick={() => handleSelectStudent(std)}
                >
                  <div>
                    <span className="fw-bold text-dark">{std.studentName}</span>
                    <Badge bg="secondary" className="ms-2">
                      USID: {std.usid}
                    </Badge>
                    <span className="text-muted ms-2 small">
                      Class: {std.class} ({std.section || "A"}) | Campus: {std.campus || "Main"}
                    </span>
                  </div>
                  <Button variant="primary" size="sm" className="rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm text-nowrap">
                    Open Cart <ArrowRight size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* VIEW 1: ACTIVE USER CARTS TABLE (Overview List) */}
      {!selectedParent && (
        <div className="cart-main-card">
          <div className="cart-main-card-header">
            <div className="d-flex align-items-center gap-2">
              <Users size={20} className="text-primary" />
              <h5 className="mb-0 fw-bold">Active User Carts ({cartsTotal})</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="input-group input-group-sm" style={{ width: "280px" }}>
                <span className="input-group-text bg-white border-end-0 text-muted">
                  <Search size={14} />
                </span>
                <Form.Control
                  type="text"
                  className="border-start-0"
                  placeholder="Filter by parent / student..."
                  value={listSearchTerm}
                  onChange={(e) => setListSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      loadActiveCarts(1, listSearchTerm);
                    }
                  }}
                />
                {listSearchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setListSearchTerm("");
                      loadActiveCarts(1, "");
                    }}
                  >
                    <X size={14} />
                  </Button>
                )}
                <Button
                  variant="outline-primary"
                  onClick={() => loadActiveCarts(1, listSearchTerm)}
                >
                  Filter
                </Button>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="cart-custom-table">
              <thead>
                <tr>
                  <th style={{ width: "90px" }}>Cart ID</th>
                  <th>Parent Information</th>
                  <th>Associated Students</th>
                  <th className="text-center">Items & Units</th>
                  <th className="text-end">Final Price (Inc. Delivery)</th>
                  <th>Last Modified</th>
                  <th className="text-end" style={{ width: "180px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingActiveCarts ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <Spinner animation="border" variant="primary" size="sm" />
                      <span className="ms-2 text-muted">Loading active carts...</span>
                    </td>
                  </tr>
                ) : activeCarts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      <ShoppingCart size={42} className="opacity-50 mb-2" />
                      <p className="mb-0 fw-semibold">No active user shopping carts found.</p>
                      <small className="text-muted">Active carts appear here once parents add uniform products.</small>
                    </td>
                  </tr>
                ) : (
                  activeCarts.map((item) => {
                    const cartCalc = calculateCartShippingAndTotal(item);
                    return (
                      <tr key={item.id}>
                        <td>
                          <Badge bg="light" text="dark" className="border px-2 py-1 fs-7">
                            #{item.id}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div className="parent-avatar-circle">
                              {(item.parent?.parentName || "U").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="fw-bold text-dark">
                                {item.parent?.parentName || `Parent #${item.parentId}`}
                              </div>
                              <div className="text-muted small d-flex flex-wrap gap-2 mt-1">
                                {(item.parent?.phoneNumber || item.parent?.phonenumber) && (
                                  <span className="d-flex align-items-center gap-1">
                                    <Phone size={12} className="text-muted" /> {item.parent.phoneNumber || item.parent.phonenumber}
                                  </span>
                                )}
                                {item.parent?.email && (
                                  <span className="d-flex align-items-center gap-1">
                                    <Mail size={12} className="text-muted" /> {item.parent.email}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {item.students?.length > 0 ? (
                            item.students.map((s, sIdx) => (
                              <div key={sIdx} className="student-badge-pill">
                                <span className="fw-semibold">{s.studentName}</span>
                                <Badge bg="primary" style={{ fontSize: "0.7rem" }}>
                                  {s.usid}
                                </Badge>
                                <span className="text-muted">({s.class})</span>
                              </div>
                            ))
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge bg="primary" className="me-1 px-2 py-1">
                            {item.totalItems} {item.totalItems === 1 ? "Item" : "Items"}
                          </Badge>
                          <div className="small text-muted mt-1">{item.totalUnits} Units</div>
                          {cartCalc.weightKg > 0 && (
                            <div
                              className="small text-muted mt-1 d-flex align-items-center justify-content-center gap-1"
                              title="Estimated Total Weight"
                            >
                              <Scale size={12} className="text-muted" />
                              <span>{cartCalc.weightKg.toFixed(2)} kg</span>
                            </div>
                          )}
                        </td>
                        <td className="text-end">
                          <div className="fw-bold text-dark" style={{ fontSize: "1.1rem" }}>
                            ₹{cartCalc.finalPrice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                            Items: ₹{cartCalc.itemsSubtotal.toFixed(2)}
                            {cartCalc.isDeliveryActive && cartCalc.deliveryDetails && (
                              <span className="text-primary ms-1 d-block">
                                + ₹{cartCalc.shippingFee.toFixed(2)} Delivery ({cartCalc.deliveryDetails})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="small text-muted">
                          <div className="d-flex align-items-center gap-1">
                            <Clock size={13} className="text-muted" />
                            {formatDateTime(item.updatedAt || item.createdAt)}
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end align-items-center gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              className="rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm text-nowrap"
                              onClick={() => handleOpenCartFromList(item)}
                              title="Manage & Edit Cart"
                            >
                              <ShoppingCart size={14} /> Open Cart
                            </Button>
                            {canDelete && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="rounded-circle d-inline-flex align-items-center justify-content-center p-0 flex-shrink-0"
                                style={{ width: "32px", height: "32px" }}
                                onClick={() => confirmClearAll(item.parentId)}
                                title="Clear Cart"
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {cartsTotalPages > 1 && (
            <div className="p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span className="small text-muted">
                Showing page {cartsPage} of {cartsTotalPages} ({cartsTotal} total active carts)
              </span>
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev
                  disabled={cartsPage <= 1}
                  onClick={() => setCartsPage((p) => Math.max(1, p - 1))}
                />
                {[...Array(cartsTotalPages)].map((_, i) => (
                  <Pagination.Item
                    key={i + 1}
                    active={i + 1 === cartsPage}
                    onClick={() => setCartsPage(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={cartsPage >= cartsTotalPages}
                  onClick={() => setCartsPage((p) => Math.min(cartsTotalPages, p + 1))}
                />
              </Pagination>
            </div>
          )}
        </div>
      )}

      {/* VIEW 2: CART DETAIL & EDITOR (Selected Parent) */}
      {selectedParent && (
        <>
          {/* Parent Profile Card */}
          <div className="parent-detail-card">
            <div className="parent-detail-header">
              <div className="d-flex align-items-center gap-2">
                <User size={18} />
                <span className="fw-bold">Parent & Account Profile</span>
              </div>
              <Badge bg="light" text="dark" className="px-2 py-1">
                Parent ID: #{selectedParent.id}
              </Badge>
            </div>
            <div className="parent-detail-body">
              <Row className="g-3">
                <Col xs={12} md={4}>
                  <div className="profile-card-block">
                    <div className="profile-icon-square">
                      <User size={18} />
                    </div>
                    <div>
                      <div className="fw-bold text-dark">{selectedParent.parentName || "Parent Name N/A"}</div>
                      <div className="text-muted small mt-1 d-flex align-items-center gap-1">
                        <Phone size={12} /> {selectedParent.phoneNumber || selectedParent.phonenumber || "No phone recorded"}
                      </div>
                      <div className="text-muted small d-flex align-items-center gap-1 mt-1">
                        <Mail size={12} /> {selectedParent.email || "No email recorded"}
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={4}>
                  <div className="profile-card-block">
                    <div className="profile-icon-square">
                      <MapPin size={18} />
                    </div>
                    <div>
                      <div className="fw-bold text-dark small mb-1">Delivery Address</div>
                      <div className="small text-muted">
                        {selectedParent.address ? (
                          <>
                            {selectedParent.address}, {selectedParent.city || ""}, {selectedParent.state || ""}{" "}
                            - {selectedParent.pincode || ""}
                          </>
                        ) : (
                          "No delivery address recorded on profile"
                        )}
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={4}>
                  <div className="profile-card-block">
                    <div className="profile-icon-square">
                      <Users size={18} />
                    </div>
                    <div>
                      <div className="fw-bold text-dark small mb-1">Active Student Context</div>
                      {selectedStudent ? (
                        <div className="student-badge-pill bg-light border border-primary text-primary">
                          <CheckCircle size={14} className="text-primary" />
                          <span className="fw-bold">{selectedStudent.studentName}</span>
                          <span className="text-muted">({selectedStudent.usid} - {selectedStudent.class})</span>
                        </div>
                      ) : (
                        <span className="small text-muted">All Linked Children</span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          {/* User Cart Items & Sticky Summary */}
          {loadingCart ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading user cart items...</p>
            </div>
          ) : cartItemsState.length === 0 ? (
            <div className="cart-main-card text-center py-5">
              <ShoppingCart size={52} className="text-muted mb-3 opacity-50" />
              <h5 className="fw-bold">This User's Shopping Cart is Empty</h5>
              <p className="text-muted small mb-4">
                You can add uniform bundles or individual products on behalf of the parent.
              </p>
              {canEdit && (
                <Button
                  variant="primary"
                  className="px-4 py-2 shadow-sm fw-semibold"
                  onClick={() => handleOpenAddModal(selectedStudent)}
                >
                  <Plus size={18} className="me-1" /> Add Bundle / Product to Cart
                </Button>
              )}
            </div>
          ) : (
            <Row className="g-4">
              {/* Items List */}
              <Col xs={12} lg={8}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <Package size={20} className="text-primary" />
                    Cart Items ({cartItemsState.length} {cartItemsState.length === 1 ? "Item" : "Items"})
                  </h5>
                  {canDelete && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="d-inline-flex align-items-center gap-1"
                      onClick={() => confirmClearAll(selectedParent.id)}
                    >
                      <Trash2 size={14} /> Clear Entire Cart
                    </Button>
                  )}
                </div>

                {cartItemsState.map((bundleItem, idx) => {
                  const itemProductsTotal = (bundleItem.products || []).reduce((acc, prod) => {
                    const unitPrice = bundleItem.isIndividualProduct
                      ? prod.unitPrice
                      : prod.bundlePrice || prod.unitPrice;
                    const qty = Number(prod.totalQuantity) || 0;
                    return acc + qty * unitPrice;
                  }, 0);
                  const itemDelivery = getItemDeliveryInfo(bundleItem, idx);
                  const itemGrandTotal = itemProductsTotal + (itemDelivery.fee || 0);

                  return (
                    <div
                      key={`${bundleItem.bundleId}-${idx}`}
                      className={`cart-bundle-card ${bundleItem.isModified ? "has-changes" : ""}`}
                    >
                      {/* Bundle Item Header */}
                      <div className="cart-bundle-header-bar">
                        <div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-bold text-dark" style={{ fontSize: "1.05rem" }}>
                              {bundleItem.bundleName}
                            </span>
                            <Badge bg={bundleItem.isIndividualProduct ? "secondary" : "primary"}>
                              {bundleItem.isIndividualProduct ? "Single Product" : "Full Bundle"}
                            </Badge>
                            {totals.isDeliveryActive && itemDelivery.fee > 0 && (
                              <Badge
                                bg="light"
                                className="d-inline-flex align-items-center gap-1 border border-primary-subtle text-primary px-2 py-1"
                                style={{ fontSize: "0.75rem" }}
                              >
                                <Truck size={12} /> Delivery: ₹{itemDelivery.fee.toFixed(2)}
                              </Badge>
                            )}
                            {bundleItem.isModified && (
                              <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1">
                                <Sparkles size={12} /> Unsaved Changes
                              </Badge>
                            )}
                          </div>
                          <div className="text-muted small mt-1">
                            Student: <span className="fw-semibold text-dark">{bundleItem.studentName}</span> (USID:{" "}
                            {bundleItem.studentUsid}, Class: {bundleItem.studentClass})
                          </div>
                        </div>

                      <div className="d-flex align-items-center gap-2">
                        {canEdit && (
                          <Button
                            variant={bundleItem.isModified ? "primary" : "success"}
                            size="sm"
                            className="d-inline-flex align-items-center gap-1 fw-semibold shadow-sm"
                            onClick={() => handleSaveItemChanges(bundleItem)}
                            disabled={bundleItem.isSaving}
                          >
                            {bundleItem.isSaving ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              <Save size={14} />
                            )}
                            {bundleItem.isModified ? "Save Changes" : "Saved"}
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => confirmDeleteItem(bundleItem)}
                            title="Remove item from cart"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Products Table Inside Bundle */}
                    <div className="table-responsive">
                      <table className="cart-custom-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th style={{ width: "150px" }}>Size</th>
                            <th style={{ width: "170px" }} className="text-center">
                              Quantity & Addon
                            </th>
                            <th className="text-end">Price</th>
                            <th className="text-end">Line Total</th>
                            {canEdit && <th style={{ width: "90px" }} className="text-center">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {bundleItem.products.map((prod) => {
                            const unitPrice = bundleItem.isIndividualProduct
                              ? prod.unitPrice
                              : prod.bundlePrice || prod.unitPrice;
                            const qty = Number(prod.totalQuantity) || 0;
                            const lineTotal = qty * unitPrice;
                            const prodWeightKg = getProductWeightKg(
                              prod.productId,
                              prod.size,
                              prod.productWeight
                            );

                            return (
                              <tr key={prod.productId}>
                                <td>
                                  <div className="d-flex align-items-center gap-3">
                                    {prod.productImage ? (
                                      <img
                                        src={prod.productImage}
                                        alt={prod.productName}
                                        className="product-img-thumb"
                                      />
                                    ) : (
                                      <div className="product-img-placeholder">
                                        <Package size={22} />
                                      </div>
                                    )}
                                    <div>
                                      <div className="fw-bold text-dark small d-flex align-items-center gap-1">
                                        {prod.productName}
                                        {prod.addonQuantity > 0 && (
                                          <Badge bg="primary" className="small" style={{ fontSize: "0.68rem" }}>
                                            +{prod.addonQuantity} Addon
                                          </Badge>
                                        )}
                                      </div>
                                      {prodWeightKg > 0 && (
                                        <div
                                          className="text-muted d-flex align-items-center gap-1 mt-0.5"
                                          style={{ fontSize: "0.75rem" }}
                                        >
                                          <Scale size={11} className="text-muted" />
                                          <span>
                                            Weight: <strong className="text-secondary">{prodWeightKg.toFixed(2)} kg</strong>
                                            {prod.baseQuantity > 0 ? ` (Base: ${prod.baseQuantity})` : ""}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>

                                {/* Size Dropdown */}
                                <td>
                                  {canEdit ? (
                                    <select
                                      className="cart-size-select"
                                      value={prod.size || ""}
                                      onChange={(e) =>
                                        handleSizeChange(bundleItem.bundleId, prod.productId, e.target.value)
                                      }
                                    >
                                      {prod.availableSizes.map((s, sIdx) => (
                                        <option key={sIdx} value={s}>
                                          {s}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <Badge bg="light" text="dark" className="border">
                                      {prod.size || "N/A"}
                                    </Badge>
                                  )}
                                </td>

                                {/* Quantity Stepper */}
                                <td className="text-center">
                                  {canEdit ? (
                                    <div className="qty-stepper-box">
                                      <button
                                        type="button"
                                        className="qty-step-btn"
                                        onClick={() =>
                                          handleQuantityChange(bundleItem.bundleId, prod.productId, -1)
                                        }
                                        disabled={Number(prod.totalQuantity) <= 0}
                                      >
                                        <Minus size={12} />
                                      </button>
                                      <input
                                        type="text"
                                        className="qty-step-input"
                                        value={prod.totalQuantity}
                                        onChange={(e) =>
                                          handleQuantityInput(bundleItem.bundleId, prod.productId, e.target.value)
                                        }
                                      />
                                      <button
                                        type="button"
                                        className="qty-step-btn"
                                        onClick={() =>
                                          handleQuantityChange(bundleItem.bundleId, prod.productId, 1)
                                        }
                                      >
                                        <Plus size={12} />
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="fw-bold">{prod.totalQuantity ?? 0}</span>
                                  )}
                                </td>

                                {/* Unit Price */}
                                <td className="text-end small">₹{unitPrice.toFixed(2)}</td>

                                {/* Line Total */}
                                <td className="text-end fw-bold text-dark small">
                                  ₹{lineTotal.toFixed(2)}
                                </td>

                                {/* Row Actions */}
                                {canEdit && (
                                  <td className="text-center align-middle">
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      {prod.addonQuantity > 0 && (
                                        <Button
                                          variant="outline-warning"
                                          size="sm"
                                          className="p-1"
                                          style={{ fontSize: "0.72rem", lineHeight: 1 }}
                                          title="Reset to base quantity"
                                          onClick={() => handleResetAddon(bundleItem.bundleId, prod.productId)}
                                        >
                                          Reset
                                        </Button>
                                      )}
                                      {!bundleItem.isIndividualProduct && bundleItem.products.length > 1 && (
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          className="p-1"
                                          title="Remove product from this bundle"
                                          onClick={() => handleRemoveProductFromBundle(bundleItem.bundleId, prod.productId)}
                                        >
                                          <Trash2 size={13} />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Delivery & Line Total Strip for this Cart Item */}
                    {totals.isDeliveryActive && itemDelivery.fee > 0 && (
                      <div className="cart-item-delivery-strip">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="d-flex align-items-center gap-1 text-dark fw-semibold">
                            <Truck size={14} className="text-primary" />
                            {bundleItem.isIndividualProduct ? "Individual Item Delivery:" : "Full Bundle Delivery:"}
                          </span>
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">
                            ₹{itemDelivery.fee.toFixed(2)}
                          </span>
                          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                            ({itemDelivery.label})
                          </span>
                        </div>
                        <div className="text-end">
                          <span className="text-muted me-2" style={{ fontSize: "0.85rem" }}>
                            Item Total (Inc. Delivery):
                          </span>
                          <strong className="text-dark" style={{ fontSize: "0.95rem" }}>
                            ₹{itemGrandTotal.toFixed(2)}
                          </strong>
                        </div>
                      </div>
                    )}

                    {/* Bundle Card Bottom Toolbar for Adding Extra Items into this Bundle */}
                    {!bundleItem.isIndividualProduct && canEdit && (
                      <div className="mt-2 pt-2 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 px-3 py-2 bg-white">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="d-inline-flex align-items-center gap-1 fw-semibold"
                          onClick={() => handleOpenAddToBundleModal(bundleItem)}
                        >
                          <Plus size={14} /> Add Product to this Bundle
                        </Button>
                        <span className="text-muted small">
                          Manage bundle items & click "Save Changes" to apply
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
              </Col>

              {/* Summary Sticky Panel */}
              <Col xs={12} lg={4}>
                <div className="cart-summary-sticky">
                  <div className="cart-summary-title">
                    <Scale size={20} className="text-primary" /> Cart Summary & Logistics
                  </div>

                  <div className="cart-summary-row">
                    <span>Total Bundles / Items</span>
                    <strong>{cartItemsState.length}</strong>
                  </div>

                  <div className="cart-summary-row">
                    <span>Total Product Units</span>
                    <strong>{totals.totalUnits}</strong>
                  </div>

                  <div className="cart-summary-row">
                    <span className="d-flex align-items-center gap-1">
                      <Scale size={14} className="text-primary" /> Estimated Total Weight
                    </span>
                    <strong>
                      {totals.totalWeight > 0 ? (
                        <>
                          {totals.totalWeight.toFixed(2)} kg{" "}
                          <span className="text-muted" style={{ fontSize: "0.8rem", fontWeight: "normal" }}>
                            ({totals.totalWeightGrams} g)
                          </span>
                        </>
                      ) : (
                        <span className="text-muted">0.00 kg</span>
                      )}
                    </strong>
                  </div>

                  <div className="cart-summary-row">
                    <span>Items Subtotal</span>
                    <strong>
                      ₹{totals.totalPrice.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </div>

                  {totals.isDeliveryActive && (
                    <>
                      {totals.bundleCount > 0 && (
                        <div className="cart-summary-row">
                          <span>
                            <span className="d-flex align-items-center gap-1">
                              <Package size={14} className="text-primary" /> Bundle Delivery ({totals.bundleCount})
                            </span>
                            <span className="text-muted d-block" style={{ fontSize: "0.75rem" }}>
                              {totals.bundleDeliveryDetails}
                            </span>
                          </span>
                          <strong className="text-primary">₹{totals.bundleShipping.toFixed(2)}</strong>
                        </div>
                      )}

                      {totals.singleItemCount > 0 && (
                        <div className="cart-summary-row">
                          <span>
                            <span className="d-flex align-items-center gap-1">
                              <Layers size={14} className="text-primary" /> Individual Items Delivery ({totals.singleItemCount})
                            </span>
                            <span className="text-muted d-block" style={{ fontSize: "0.75rem" }}>
                              {totals.singleDeliveryDetails}
                            </span>
                          </span>
                          <strong className="text-primary">₹{totals.singleShipping.toFixed(2)}</strong>
                        </div>
                      )}

                      <div className="cart-summary-row border-top pt-2">
                        <span>
                          <span className="d-flex align-items-center gap-1 fw-semibold text-dark">
                            <Truck size={14} className="text-primary" /> Total Courier Shipping (Est.)
                          </span>
                          <span className="text-muted d-block" style={{ fontSize: "0.75rem" }}>
                            {totals.deliveryDetails}
                          </span>
                        </span>
                        <strong className="text-primary fs-6">₹{totals.shippingCost.toFixed(2)}</strong>
                      </div>
                    </>
                  )}

                  <div className="cart-grand-total-row">
                    <div>
                      <div>Final Grand Total</div>
                      <div className="text-muted fw-normal" style={{ fontSize: "0.75rem" }}>
                        {totals.isDeliveryActive ? "Includes Items + Courier Delivery" : "Items Subtotal"}
                      </div>
                    </div>
                    <span className="text-primary">
                      ₹{totals.grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {canEdit && (
                    <div className="mt-4 d-grid gap-2">
                      <Button
                        variant="primary"
                        className="py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                        onClick={() => handleOpenAddModal(selectedStudent, "bundle")}
                      >
                        <Plus size={18} /> Add Bundle / Single Item
                      </Button>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </>
      )}

      {/* Delete / Clear Confirmation Modal */}
      <ConfirmModal
        show={showConfirmDelete}
        onHide={() => setShowConfirmDelete(false)}
        onConfirm={handleExecuteDelete}
        title={isClearingAll ? "Clear Entire Cart" : "Remove Cart Item"}
        message={
          isClearingAll
            ? "Are you sure you want to remove all items from this user's cart? This action cannot be undone."
            : `Are you sure you want to remove "${itemToDelete?.bundleName}" from the cart?`
        }
        confirmText="Yes, Remove"
        cancelText="Cancel"
      />

      {/* Add Bundle / Single Item to Cart Modal */}
      <div
        className={`modal fade ${showAddModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{ backgroundColor: showAddModal ? "rgba(0,0,0,0.5)" : "transparent", pointerEvents: showAddModal ? "auto" : "none" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-3">
            <div className="modal-header border-bottom pb-3">
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2 text-dark">
                <Plus size={20} className="text-primary" />
                Add to Cart ({modalMode === "bundle" ? "Full Uniform Bundle" : "Single Product"})
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowAddModal(false)}
              ></button>
            </div>

            <div className="modal-body py-4">
              {modalStudent && (
                <Alert variant="info" className="py-2 px-3 mb-3 small d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Student:</strong> {modalStudent.studentName} ({modalStudent.usid}) |{" "}
                    <strong>Class:</strong> {modalStudent.class} | <strong>Gender:</strong> {modalStudent.gender}
                  </div>
                </Alert>
              )}

              {/* Mode Switcher: Bundles vs Single Items */}
              <div className="d-flex gap-2 mb-3">
                <Button
                  variant={modalMode === "bundle" ? "primary" : "outline-primary"}
                  size="sm"
                  className="fw-semibold px-3 d-inline-flex align-items-center gap-1"
                  onClick={() => {
                    setModalMode("bundle");
                    setSelectedItemToAdd(null);
                  }}
                >
                  <Package size={14} /> Full Uniform Bundles ({availableBundles.length})
                </Button>
                <Button
                  variant={modalMode === "product" ? "primary" : "outline-primary"}
                  size="sm"
                  className="fw-semibold px-3 d-inline-flex align-items-center gap-1"
                  onClick={() => {
                    setModalMode("product");
                    setSelectedItemToAdd(null);
                  }}
                >
                  <Layers size={14} /> Single Items / Products ({availableSingleItems.length})
                </Button>
              </div>

              {/* Source Switcher: All Catalog vs Assigned Only */}
              <div className="d-flex gap-2 mb-3">
                <Button
                  variant={itemSourceFilter === "all" ? "secondary" : "outline-secondary"}
                  size="sm"
                  className="px-3 small"
                  onClick={() => {
                    setItemSourceFilter("all");
                    setSelectedItemToAdd(null);
                  }}
                >
                  All Catalog {modalMode === "bundle" ? `Bundles (${availableBundles.length})` : `Single Items (${availableSingleItems.length})`}
                </Button>
                <Button
                  variant={itemSourceFilter === "assigned" ? "secondary" : "outline-secondary"}
                  size="sm"
                  className="px-3 small"
                  onClick={() => {
                    setItemSourceFilter("assigned");
                    setSelectedItemToAdd(null);
                  }}
                >
                  Assigned to Student {modalMode === "bundle" ? `(${assignedBundles.length})` : `(${assignedSingleItems.length})`}
                </Button>
              </div>

              {/* Instant Search Bar */}
              <div className="mb-3 position-relative">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light border-end-0">
                    <Search size={14} className="text-muted" />
                  </span>
                  <Form.Control
                    type="text"
                    placeholder={`Search ${modalMode === "bundle" ? "bundles" : "single items"} by name, SKU, class, gender...`}
                    value={modalSearchTerm}
                    onChange={(e) => setModalSearchTerm(e.target.value)}
                    className="border-start-0 ps-0"
                  />
                  {modalSearchTerm && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setModalSearchTerm("")}
                    >
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>

              {fetchingCatalog ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" size="sm" />
                  <div className="small text-muted mt-2">Loading catalog items...</div>
                </div>
              ) : (
                <>
                  {/* Item Selector Dropdown */}
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">
                      Select {modalMode === "bundle" ? "Bundle" : "Single Item"} ({filteredModalItems.length} available)
                    </Form.Label>
                    <Form.Select
                      value={selectedItemToAdd?.bundle_id || selectedItemToAdd?.id || ""}
                      onChange={(e) => {
                        const id = parseInt(e.target.value, 10);
                        const found = filteredModalItems.find((b) => (b.bundle_id || b.id) === id);
                        if (found) handleSelectItemToAdd(found);
                      }}
                    >
                      <option value="">-- Choose {modalMode === "bundle" ? "a Bundle" : "a Single Item"} --</option>
                      {filteredModalItems.map((item) => {
                        const classLabel = item.applicableClasses ? ` [Class: ${item.applicableClasses}]` : "";
                        const genderLabel = item.gender ? ` [${item.gender}]` : "";
                        return (
                          <option key={item.bundle_id || item.id} value={item.bundle_id || item.id}>
                            {item.bundle_name || item.name} — ₹{item.bundle_total || item.totalPrice || item.price}{classLabel}{genderLabel}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>

                  {/* Items and Sizes Configuration */}
                  {selectedItemToAdd && (
                    <div className="border rounded p-3 bg-light mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="fw-bold mb-0 text-dark">
                          {selectedItemToAdd.bundle_name || selectedItemToAdd.name}
                        </h6>
                        {selectedItemToAdd.applicableClasses && (
                          <Badge bg="secondary" className="small">
                            Class: {selectedItemToAdd.applicableClasses}
                          </Badge>
                        )}
                      </div>

                      <div className="table-responsive bg-white rounded border mt-2">
                        <table className="cart-custom-table mb-0">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th style={{ width: "150px" }}>Size</th>
                              <th style={{ width: "130px" }} className="text-center">Quantity</th>
                              <th className="text-end" style={{ width: "110px" }}>Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedItemToAdd.products || []).map((p) => {
                              let sizes = [];
                              const rawSizes = p.available_sizes || p.availableSizes;
                              if (Array.isArray(rawSizes)) sizes = rawSizes;
                              else if (typeof rawSizes === "string") {
                                try {
                                  const parsed = JSON.parse(rawSizes);
                                  sizes = Array.isArray(parsed) ? parsed : rawSizes.split(",").map((s) => s.trim());
                                } catch {
                                  sizes = rawSizes.split(",").map((s) => s.trim());
                                }
                              }
                              sizes = sizes.map((s) => String(s).trim()).filter(Boolean);
                              if (p.size && !sizes.includes(p.size)) sizes = [p.size, ...sizes];
                              if (p.product_size && !sizes.includes(p.product_size)) sizes = [p.product_size, ...sizes];
                              if (sizes.length === 0) sizes = ["Free-NA"];

                              const currentSize = modalProductSizes[p.product_id] || p.product_size || p.size || sizes[0] || "Free-NA";
                              const currentQty = modalProductQuantities[p.product_id] || p.quantity || 1;
                              const unitPrice = modalMode === "bundle"
                                ? (p.bundle_price || p.unit_price)
                                : (p.unit_price || p.price || selectedItemToAdd.totalPrice || 0);

                              return (
                                <tr key={p.product_id}>
                                  <td className="align-middle">
                                    <div className="d-flex align-items-center gap-2">
                                      {p.imageUrl && (
                                        <img
                                          src={p.imageUrl}
                                          alt={p.name}
                                          style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "4px" }}
                                        />
                                      )}
                                      <div>
                                        <div className="fw-semibold small">{p.name || p.product_name}</div>
                                        {p.sku && <div className="text-muted" style={{ fontSize: "0.75rem" }}>SKU: {p.sku}</div>}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="align-middle">
                                    <select
                                      className="cart-size-select form-select form-select-sm"
                                      value={currentSize}
                                      onChange={(e) =>
                                        setModalProductSizes((prev) => ({
                                          ...prev,
                                          [p.product_id]: e.target.value,
                                        }))
                                      }
                                    >
                                      {sizes.map((s, sIdx) => (
                                        <option key={sIdx} value={s}>
                                          {s}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="align-middle text-center">
                                    <div className="d-inline-flex align-items-center gap-1">
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="py-0 px-2"
                                        style={{ height: "28px" }}
                                        onClick={() =>
                                          setModalProductQuantities((prev) => ({
                                            ...prev,
                                            [p.product_id]: Math.max(1, (prev[p.product_id] || 1) - 1),
                                          }))
                                        }
                                      >
                                        -
                                      </Button>
                                      <Form.Control
                                        type="number"
                                        min="1"
                                        size="sm"
                                        value={currentQty}
                                        onChange={(e) =>
                                          setModalProductQuantities((prev) => ({
                                            ...prev,
                                            [p.product_id]: Math.max(1, parseInt(e.target.value, 10) || 1),
                                          }))
                                        }
                                        style={{ width: "55px", textAlign: "center", height: "28px" }}
                                      />
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="py-0 px-2"
                                        style={{ height: "28px" }}
                                        onClick={() =>
                                          setModalProductQuantities((prev) => ({
                                            ...prev,
                                            [p.product_id]: (prev[p.product_id] || 1) + 1,
                                          }))
                                        }
                                      >
                                        +
                                      </Button>
                                    </div>
                                  </td>
                                  <td className="align-middle text-end small fw-bold">
                                    ₹{(currentQty * (parseFloat(unitPrice) || 0)).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer border-top pt-3">
              <Button
                variant="outline-secondary"
                onClick={() => setShowAddModal(false)}
                disabled={addingItem}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleExecuteAddToCart}
                disabled={!selectedItemToAdd || addingItem}
                className="fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
              >
                {addingItem ? <Spinner size="sm" animation="border" /> : <Plus size={16} />}
                Add to User's Cart
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Add Product into an existing Cart Bundle */}
      <div
        className={`modal fade ${showAddToBundleModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showAddToBundleModal ? "rgba(0,0,0,0.5)" : "transparent",
          pointerEvents: showAddToBundleModal ? "auto" : "none",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-3">
            <div className="modal-header border-bottom pb-3">
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2 text-dark">
                <Plus size={20} className="text-primary" />
                Add Item to "{targetBundleItem?.bundleName}"
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowAddToBundleModal(false)}
              ></button>
            </div>

            <div className="modal-body py-4">
              <div className="mb-3">
                <Form.Label className="small fw-bold">Search Catalog Product</Form.Label>
                <div className="input-group input-group-sm mb-2">
                  <span className="input-group-text bg-light border-end-0">
                    <Search size={14} className="text-muted" />
                  </span>
                  <Form.Control
                    type="text"
                    placeholder="Type product name or SKU..."
                    value={extraProductSearch}
                    onChange={(e) => setExtraProductSearch(e.target.value)}
                    className="border-start-0 ps-0"
                  />
                  {extraProductSearch && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setExtraProductSearch("")}
                    >
                      <X size={14} />
                    </Button>
                  )}
                </div>

                <Form.Select
                  value={selectedExtraProduct?.product_id || ""}
                  onChange={(e) => {
                    const id = parseInt(e.target.value, 10);
                    const prod = extraProductList.find((p) => p.product_id === id);
                    if (prod) {
                      setSelectedExtraProduct(prod);
                      const sizes = prod.available_sizes || prod.availableSizes || ["Free-NA"];
                      setSelectedExtraSize(sizes[0] || "Free-NA");
                    } else {
                      setSelectedExtraProduct(null);
                    }
                  }}
                >
                  <option value="">-- Choose Product to Add to Bundle --</option>
                  {extraProductList
                    .filter((p) => {
                      if (!extraProductSearch.trim()) return true;
                      const term = extraProductSearch.toLowerCase().trim();
                      return (
                        (p.name || p.product_name || "").toLowerCase().includes(term) ||
                        (p.sku || "").toLowerCase().includes(term)
                      );
                    })
                    .map((p) => (
                      <option key={p.product_id} value={p.product_id}>
                        {p.name || p.product_name} (₹{p.bundle_price || p.unit_price})
                      </option>
                    ))}
                </Form.Select>
              </div>

              {selectedExtraProduct && (
                <div className="bg-light p-3 rounded border">
                  <Row className="g-3">
                    <Col xs={6}>
                      <Form.Label className="small fw-bold">Size</Form.Label>
                      <Form.Select
                        size="sm"
                        value={selectedExtraSize}
                        onChange={(e) => setSelectedExtraSize(e.target.value)}
                      >
                        {(selectedExtraProduct.available_sizes || selectedExtraProduct.availableSizes || ["Free-NA"]).map(
                          (s, sIdx) => (
                            <option key={sIdx} value={s}>
                              {s}
                            </option>
                          )
                        )}
                      </Form.Select>
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small fw-bold">Quantity</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        size="sm"
                        value={selectedExtraQty}
                        onChange={(e) => setSelectedExtraQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      />
                    </Col>
                  </Row>

                  <div className="mt-3 text-end fw-bold small text-primary">
                    Added Price: ₹{(selectedExtraQty * (parseFloat(selectedExtraProduct.bundle_price || selectedExtraProduct.unit_price) || 0)).toFixed(2)}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer border-top pt-3">
              <Button
                variant="outline-secondary"
                onClick={() => setShowAddToBundleModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmAddProductToBundle}
                disabled={!selectedExtraProduct}
                className="fw-semibold d-inline-flex align-items-center gap-2"
              >
                <Plus size={16} /> Add to Bundle
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartManagement;
